/* eslint-disable import/export */
// ============================================================================
// VALUE OBJECTS
// ============================================================================

/**
 * Money - Immutable value object for monetary amounts
 * Stores cents internally to avoid floating point issues
 */
export class Money {
  private constructor(
    private readonly cents: number,
    private readonly currency: string = 'MXN'
  ) {
    if (!Number.isFinite(cents) || !Number.isInteger(cents)) {
      throw new Error('Money cents must be a finite integer');
    }
  }

  // Factory methods
  static fromCents(cents: number, currency = 'MXN'): Money {
    return new Money(Math.round(cents), currency);
  }

  static fromDecimal(amount: number, currency = 'MXN'): Money {
    return new Money(Math.round(amount * 100), currency);
  }

  static zero(currency = 'MXN'): Money {
    return new Money(0, currency);
  }

  // Getters
  toCents(): number {
    return this.cents;
  }

  toDecimal(): number {
    return this.cents / 100;
  }

  getCurrency(): string {
    return this.currency;
  }

  // Formatting
  format(locale = 'es-MX'): string {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(this.toDecimal());
  }

  formatCompact(locale = 'es-MX'): string {
    const decimal = this.toDecimal();
    if (Math.abs(decimal) >= 1_000_000) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: this.currency,
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(decimal);
    }
    return this.format(locale);
  }

  // Arithmetic (returns new Money instances)
  add(other: Money): Money {
    this.assertSameCurrency(other);
    return new Money(this.cents + other.cents, this.currency);
  }

  subtract(other: Money): Money {
    this.assertSameCurrency(other);
    return new Money(this.cents - other.cents, this.currency);
  }

  multiply(factor: number): Money {
    return new Money(Math.round(this.cents * factor), this.currency);
  }

  divide(divisor: number): Money {
    if (divisor === 0) throw new Error('Cannot divide by zero');
    return new Money(Math.round(this.cents / divisor), this.currency);
  }

  negate(): Money {
    return new Money(-this.cents, this.currency);
  }

  abs(): Money {
    return new Money(Math.abs(this.cents), this.currency);
  }

  // Comparisons
  isZero(): boolean {
    return this.cents === 0;
  }

  isPositive(): boolean {
    return this.cents > 0;
  }

  isNegative(): boolean {
    return this.cents < 0;
  }

  equals(other: Money): boolean {
    return this.cents === other.cents && this.currency === other.currency;
  }

  greaterThan(other: Money): boolean {
    this.assertSameCurrency(other);
    return this.cents > other.cents;
  }

  greaterThanOrEqual(other: Money): boolean {
    this.assertSameCurrency(other);
    return this.cents >= other.cents;
  }

  lessThan(other: Money): boolean {
    this.assertSameCurrency(other);
    return this.cents < other.cents;
  }

  lessThanOrEqual(other: Money): boolean {
    this.assertSameCurrency(other);
    return this.cents <= other.cents;
  }

  private assertSameCurrency(other: Money): void {
    if (this.currency !== other.currency) {
      throw new Error(`Currency mismatch: ${this.currency} vs ${other.currency}`);
    }
  }

  // Serialization
  toJSON(): { cents: number; currency: string } {
    return { cents: this.cents, currency: this.currency };
  }

  static fromJSON(json: { cents: number; currency: string }): Money {
    return Money.fromCents(json.cents, json.currency);
  }
}

// ============================================================================
// RESULT TYPE (Error Handling)
// ============================================================================
// Definido en `./result`, un módulo hoja. Vivía aquí, pero `error-handling` lo
// importaba de este índice para re-exportarlo mientras este índice hacía
// `export *` de `error-handling`: ese ciclo reventaba la pila en runtime al
// usar `Ok`/`Err`/`isOk`.
export * from './result';

// ============================================================================
// ENUMS
// ============================================================================

// --- Roles & Permissions ---
export enum Role {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  COMPLIANCE_OFFICER = 'compliance_officer',
  SUPPORT = 'support',
  VIEWER = 'viewer',
  CLIENT = 'client',
  // Backoffice roles (backend assigns via UserRole)
  BO_CEO = 'bo_ceo',
  BO_CFO = 'bo_cfo',
  BO_CTO = 'bo_cto',
  BO_CCO = 'bo_cco',
  BO_OPS_MANAGER = 'bo_ops_manager',
  BO_KYC_REVIEWER = 'bo_kyc_reviewer',
  BO_KYC_MANAGER = 'bo_kyc_manager',
  BO_COMPLIANCE_OFFICER = 'bo_compliance_officer',
  BO_COMPLIANCE_ANALYST = 'bo_compliance_analyst',
  BO_UIF_OFFICER = 'bo_uif_officer',
  BO_FRAUD_ANALYST = 'bo_fraud_analyst',
  BO_FRAUD_MANAGER = 'bo_fraud_manager',
  BO_FINANCE_ACCOUNTANT = 'bo_finance_accountant',
  BO_FINANCE_CONTROLLER = 'bo_finance_controller',
  BO_TREASURY = 'bo_treasury',
  BO_SUPPORT_L1 = 'bo_support_l1',
  BO_SUPPORT_L2 = 'bo_support_l2',
  BO_DEVELOPER = 'bo_developer',
  SYSTEM_ADMIN = 'system_admin',
}

export enum Permission {
  // Parties
  VIEW_PARTIES = 'view_parties',
  EDIT_PARTIES = 'edit_parties',
  BLOCK_PARTIES = 'block_parties',

  // Transfers
  VIEW_TRANSFERS = 'view_transfers',
  APPROVE_TRANSFERS = 'approve_transfers',
  CANCEL_TRANSFERS = 'cancel_transfers',

  // Tesorería y operación
  /**
   * Cuentas internas y sus holds. Iba pegado a VIEW_TRANSFERS, y por eso a
   * cumplimiento le aparecía "Cuentas" en el menú mientras el backend le
   * respondía 403: el catálogo RBAC no le concede ningún `bo_accounts.*`.
   * Ver transacciones y administrar cuentas son cosas distintas.
   */
  VIEW_ACCOUNTS = 'view_accounts',
  /** Conciliación y discrepancias: tesorería/operación, no cumplimiento. */
  VIEW_RECONCILIATION = 'view_reconciliation',

  // Compliance
  VIEW_ALERTS = 'view_alerts',
  RESOLVE_ALERTS = 'resolve_alerts',
  ESCALATE_ALERTS = 'escalate_alerts',
  /**
   * Firmar el reporte a la UIF. Es cumplimiento senior: el backend lo exige con
   * `bo_compliance.approve_uif_report`. Los analistas investigan y resuelven,
   * pero no reportan.
   */
  REPORT_TO_UIF = 'report_to_uif',
  /** Cola de verificaciones KYC. Su audiencia es el revisor, no sólo cumplimiento. */
  VIEW_KYC_QUEUE = 'view_kyc_queue',
  /** Coincidencias de listas, debida diligencia reforzada e incidentes. */
  VIEW_COMPLIANCE_CASES = 'view_compliance_cases',
  /** Reportes regulatorios (CNBV, CONDUSEF, Banxico). Ver ≠ enviar. */
  VIEW_REGULATORY_REPORTS = 'view_regulatory_reports',
  /**
   * Ver el backlog de sincronización PLD: expedientes capturados, lotes y
   * resultado técnico del envío al proveedor. Backend:
   * `bo_compliance.view_pld_sync`.
   */
  VIEW_PLD_SYNC = 'view_pld_sync',
  /**
   * Capturar expedientes y despachar lotes al proveedor PLD. Backend:
   * `bo_compliance.manage_pld_sync`. Un ACK del proveedor no aprueba al
   * cliente: la decisión sigue siendo del oficial.
   */
  MANAGE_PLD_SYNC = 'manage_pld_sync',
  VIEW_REPORTS = 'view_reports',

  // Reportes programados (RFC 0035)
  /**
   * Ver programaciones de reportes (transacciones, comisiones, estados de
   * cuenta) y su historial de envíos. Backend: `bo_reports.view_financial` o
   * `bo_reports.schedule`. `view_operational` NO lo abre: los reportes llevan
   * saldos y movimientos por cuenta.
   */
  VIEW_SCHEDULED_REPORTS = 'view_scheduled_reports',
  /** Crear, editar, pausar y ejecutar programaciones. Backend: `bo_reports.schedule`. */
  MANAGE_SCHEDULED_REPORTS = 'manage_scheduled_reports',

  // Documentos
  /** Consultar y descargar documentos generados (expedientes, contratos, NOM-151). */
  VIEW_DOCUMENTS = 'view_documents',
  /**
   * Editar plantillas y administrar los grupos de firma JAAK. Separado de
   * VIEW_DOCUMENTS a propósito: publicar una versión invalida el template JAAK
   * de los grupos afectados y la firma de prueba consume cuota real del
   * proveedor. Leer un expediente no debe implicar poder hacer eso.
   */
  MANAGE_DOCUMENT_TEMPLATES = 'manage_document_templates',

  // System
  MANAGE_NOTICES = 'manage_notices',
  VIEW_AUDIT_LOG = 'view_audit_log',
  MANAGE_USERS = 'manage_users',
  MANAGE_SETTINGS = 'manage_settings',
}

// --- KYC ---
export enum KYCLevel {
  NONE = 0,
  BASIC = 1,
  INTERMEDIATE = 2,
  FULL = 3,
}

// --- Party Status ---
export enum PartyStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  BLOCKED = 'blocked',
  SUSPENDED = 'suspended',
  CLOSED = 'closed',
}

// --- Transfer Status ---
export enum TransferStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  REVERSED = 'reversed',
}

export enum TransferType {
  SPEI = 'spei',
  INTERNAL = 'internal',
}

// --- Alerts (Compliance) ---
export enum AlertType {
  UNUSUAL_AMOUNT = 'unusual_amount',
  VELOCITY = 'velocity',
  BLOCKED_RECIPIENT = 'blocked_recipient',
  HIGH_RISK_COUNTRY = 'high_risk_country',
  PEP_MATCH = 'pep_match',
  SANCTION_MATCH = 'sanction_match',
  PATTERN = 'pattern',
}

export enum AlertSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

export enum AlertStatus {
  PENDING = 'pending',
  INVESTIGATING = 'investigating',
  ESCALATED = 'escalated',
  RESOLVED = 'resolved',
  DISMISSED = 'dismissed',
}

// --- System Notices ---
export enum NoticeType {
  MAINTENANCE = 'maintenance',
  ANNOUNCEMENT = 'announcement',
  FEATURE = 'feature',
  SECURITY = 'security',
  URGENT = 'urgent',
}

export enum NoticeSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
}

export enum NoticeStatus {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  ACTIVE = 'active',
  EXPIRED = 'expired',
}

export enum NoticeTarget {
  ALL = 'all',
  CLIENTS = 'clients',
  BACKOFFICE = 'backoffice',
}

// --- Audit ---
export enum AuditAction {
  // Auth
  LOGIN = 'login',
  LOGOUT = 'logout',
  LOGIN_FAILED = 'login_failed',
  PASSWORD_RESET = 'password_reset',
  MFA_ENABLED = 'mfa_enabled',
  MFA_DISABLED = 'mfa_disabled',

  // Transfers
  TRANSFER_CREATED = 'transfer_created',
  TRANSFER_APPROVED = 'transfer_approved',
  TRANSFER_CANCELLED = 'transfer_cancelled',
  TRANSFER_FAILED = 'transfer_failed',

  // Parties
  PARTY_CREATED = 'party_created',
  PARTY_UPDATED = 'party_updated',
  PARTY_BLOCKED = 'party_blocked',
  PARTY_UNBLOCKED = 'party_unblocked',
  KYC_UPDATED = 'kyc_updated',

  // Compliance
  ALERT_CREATED = 'alert_created',
  ALERT_RESOLVED = 'alert_resolved',
  ALERT_ESCALATED = 'alert_escalated',

  // System
  NOTICE_CREATED = 'notice_created',
  NOTICE_ACTIVATED = 'notice_activated',
  NOTICE_DELETED = 'notice_deleted',
  SETTINGS_UPDATED = 'settings_updated',
}

export enum AuditCategory {
  AUTH = 'auth',
  TRANSFERS = 'transfers',
  PARTIES = 'parties',
  COMPLIANCE = 'compliance',
  NOTICES = 'notices',
  SYSTEM = 'system',
}

// ============================================================================
// LABELS (Spanish)
// ============================================================================

export const ROLE_LABELS: Record<Role, string> = {
  [Role.SUPER_ADMIN]: 'Super Administrador',
  [Role.ADMIN]: 'Administrador',
  [Role.COMPLIANCE_OFFICER]: 'Oficial de Cumplimiento',
  [Role.SUPPORT]: 'Soporte',
  [Role.VIEWER]: 'Observador',
  [Role.CLIENT]: 'Cliente',
  [Role.BO_CEO]: 'Director General',
  [Role.BO_CFO]: 'Director Financiero',
  [Role.BO_CTO]: 'Director de Tecnología',
  [Role.BO_CCO]: 'Director de Cumplimiento',
  [Role.BO_OPS_MANAGER]: 'Gerente de Operaciones',
  [Role.BO_KYC_REVIEWER]: 'Revisor KYC',
  [Role.BO_KYC_MANAGER]: 'Gerente KYC',
  [Role.BO_COMPLIANCE_OFFICER]: 'Oficial de Cumplimiento BO',
  [Role.BO_COMPLIANCE_ANALYST]: 'Analista de Cumplimiento',
  [Role.BO_UIF_OFFICER]: 'Oficial UIF',
  [Role.BO_FRAUD_ANALYST]: 'Analista de Fraude',
  [Role.BO_FRAUD_MANAGER]: 'Gerente de Fraude',
  [Role.BO_FINANCE_ACCOUNTANT]: 'Contador',
  [Role.BO_FINANCE_CONTROLLER]: 'Contralor Financiero',
  [Role.BO_TREASURY]: 'Tesorería',
  [Role.BO_SUPPORT_L1]: 'Soporte Nivel 1',
  [Role.BO_SUPPORT_L2]: 'Soporte Nivel 2',
  [Role.BO_DEVELOPER]: 'Desarrollador',
  [Role.SYSTEM_ADMIN]: 'Administrador de Sistema',
};

export const KYC_LEVEL_LABELS: Record<KYCLevel, string> = {
  [KYCLevel.NONE]: 'Sin verificar',
  [KYCLevel.BASIC]: 'Básico',
  [KYCLevel.INTERMEDIATE]: 'Intermedio',
  [KYCLevel.FULL]: 'Completo',
};

export const PARTY_STATUS_LABELS: Record<PartyStatus, string> = {
  [PartyStatus.PENDING]: 'Pendiente',
  [PartyStatus.ACTIVE]: 'Activo',
  [PartyStatus.BLOCKED]: 'Bloqueado',
  [PartyStatus.SUSPENDED]: 'Suspendido',
  [PartyStatus.CLOSED]: 'Cerrado',
};

export const PARTY_STATUS_VARIANTS: Record<
  PartyStatus,
  'default' | 'success' | 'warning' | 'error'
> = {
  [PartyStatus.PENDING]: 'warning',
  [PartyStatus.ACTIVE]: 'success',
  [PartyStatus.BLOCKED]: 'error',
  [PartyStatus.SUSPENDED]: 'warning',
  [PartyStatus.CLOSED]: 'default',
};

export const TRANSFER_STATUS_LABELS: Record<TransferStatus, string> = {
  [TransferStatus.PENDING]: 'Pendiente',
  [TransferStatus.PROCESSING]: 'Procesando',
  [TransferStatus.COMPLETED]: 'Completada',
  [TransferStatus.FAILED]: 'Fallida',
  [TransferStatus.CANCELLED]: 'Cancelada',
  [TransferStatus.REVERSED]: 'Reversada',
};

export const TRANSFER_STATUS_VARIANTS: Record<
  TransferStatus,
  'default' | 'success' | 'warning' | 'error' | 'primary'
> = {
  [TransferStatus.PENDING]: 'warning',
  [TransferStatus.PROCESSING]: 'primary',
  [TransferStatus.COMPLETED]: 'success',
  [TransferStatus.FAILED]: 'error',
  [TransferStatus.CANCELLED]: 'default',
  [TransferStatus.REVERSED]: 'warning',
};

export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  [AlertType.UNUSUAL_AMOUNT]: 'Monto inusual',
  [AlertType.VELOCITY]: 'Alta frecuencia',
  [AlertType.BLOCKED_RECIPIENT]: 'Destinatario bloqueado',
  [AlertType.HIGH_RISK_COUNTRY]: 'País de alto riesgo',
  [AlertType.PEP_MATCH]: 'Coincidencia PEP',
  [AlertType.SANCTION_MATCH]: 'Lista de sanciones',
  [AlertType.PATTERN]: 'Patrón sospechoso',
};

export const ALERT_SEVERITY_LABELS: Record<AlertSeverity, string> = {
  [AlertSeverity.LOW]: 'Baja',
  [AlertSeverity.MEDIUM]: 'Media',
  [AlertSeverity.HIGH]: 'Alta',
  [AlertSeverity.CRITICAL]: 'Crítica',
};

export const ALERT_SEVERITY_STYLES: Record<AlertSeverity, string> = {
  [AlertSeverity.LOW]: 'bg-gray-100 text-gray-800',
  [AlertSeverity.MEDIUM]: 'bg-warning-100 text-warning-800',
  [AlertSeverity.HIGH]: 'bg-error-100 text-error-800',
  [AlertSeverity.CRITICAL]: 'bg-red-600 text-white',
};

export const ALERT_STATUS_LABELS: Record<AlertStatus, string> = {
  [AlertStatus.PENDING]: 'Pendiente',
  [AlertStatus.INVESTIGATING]: 'En revisión',
  [AlertStatus.ESCALATED]: 'Escalada',
  [AlertStatus.RESOLVED]: 'Resuelta',
  [AlertStatus.DISMISSED]: 'Descartada',
};

export const NOTICE_TYPE_LABELS: Record<NoticeType, string> = {
  [NoticeType.MAINTENANCE]: 'Mantenimiento',
  [NoticeType.ANNOUNCEMENT]: 'Anuncio',
  [NoticeType.FEATURE]: 'Nueva función',
  [NoticeType.SECURITY]: 'Seguridad',
  [NoticeType.URGENT]: 'Urgente',
};

export const NOTICE_STATUS_LABELS: Record<NoticeStatus, string> = {
  [NoticeStatus.DRAFT]: 'Borrador',
  [NoticeStatus.SCHEDULED]: 'Programado',
  [NoticeStatus.ACTIVE]: 'Activo',
  [NoticeStatus.EXPIRED]: 'Expirado',
};

export const NOTICE_SEVERITY_STYLES: Record<
  NoticeSeverity,
  { bg: string; border: string; text: string; icon: string }
> = {
  [NoticeSeverity.INFO]: {
    bg: 'bg-primary-50',
    border: 'border-primary-200',
    text: 'text-primary-800',
    icon: 'ℹ️',
  },
  [NoticeSeverity.WARNING]: {
    bg: 'bg-warning-50',
    border: 'border-warning-200',
    text: 'text-warning-800',
    icon: '⚠️',
  },
  [NoticeSeverity.ERROR]: {
    bg: 'bg-error-50',
    border: 'border-error-200',
    text: 'text-error-800',
    icon: '🚨',
  },
};

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  [AuditAction.LOGIN]: 'Inicio de sesión',
  [AuditAction.LOGOUT]: 'Cierre de sesión',
  [AuditAction.LOGIN_FAILED]: 'Intento fallido de login',
  [AuditAction.PASSWORD_RESET]: 'Reseteo de contraseña',
  [AuditAction.MFA_ENABLED]: 'MFA activado',
  [AuditAction.MFA_DISABLED]: 'MFA desactivado',
  [AuditAction.TRANSFER_CREATED]: 'Transferencia creada',
  [AuditAction.TRANSFER_APPROVED]: 'Transferencia aprobada',
  [AuditAction.TRANSFER_CANCELLED]: 'Transferencia cancelada',
  [AuditAction.TRANSFER_FAILED]: 'Transferencia fallida',
  [AuditAction.PARTY_CREATED]: 'Cliente creado',
  [AuditAction.PARTY_UPDATED]: 'Cliente actualizado',
  [AuditAction.PARTY_BLOCKED]: 'Cliente bloqueado',
  [AuditAction.PARTY_UNBLOCKED]: 'Cliente desbloqueado',
  [AuditAction.KYC_UPDATED]: 'KYC actualizado',
  [AuditAction.ALERT_CREATED]: 'Alerta creada',
  [AuditAction.ALERT_RESOLVED]: 'Alerta resuelta',
  [AuditAction.ALERT_ESCALATED]: 'Alerta escalada',
  [AuditAction.NOTICE_CREATED]: 'Aviso creado',
  [AuditAction.NOTICE_ACTIVATED]: 'Aviso activado',
  [AuditAction.NOTICE_DELETED]: 'Aviso eliminado',
  [AuditAction.SETTINGS_UPDATED]: 'Configuración actualizada',
};

export const AUDIT_CATEGORY_LABELS: Record<AuditCategory, string> = {
  [AuditCategory.AUTH]: 'Autenticación',
  [AuditCategory.TRANSFERS]: 'Transferencias',
  [AuditCategory.PARTIES]: 'Clientes',
  [AuditCategory.COMPLIANCE]: 'Cumplimiento',
  [AuditCategory.NOTICES]: 'Avisos',
  [AuditCategory.SYSTEM]: 'Sistema',
};

// ============================================================================
// ROLE PERMISSIONS
// ============================================================================

/**
 * RESPALDO, no fuente de verdad.
 *
 * La autoridad es `/api/v1/admin/me`, que deriva estas capacidades de los
 * permisos RBAC reales (ver `feature_permissions` en `me_controller.rb`). Esta
 * tabla sólo entra cuando el servidor no envió capacidades.
 *
 * Al agregar una llave al enum `Permission` hay que añadirla TAMBIÉN al puente
 * del backend; si no, el módulo funciona sólo para los roles escritos a mano
 * aquí y falla en silencio para los demás. La prueba
 * `spec/requests/api/v1/admin/me_feature_permissions_spec.rb` verifica que el
 * puente emita la lista completa.
 */
const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  [Role.SUPER_ADMIN]: Object.values(Permission),

  [Role.ADMIN]: [
    Permission.VIEW_PARTIES,
    Permission.EDIT_PARTIES,
    Permission.BLOCK_PARTIES,
    Permission.VIEW_TRANSFERS,
    Permission.APPROVE_TRANSFERS,
    Permission.CANCEL_TRANSFERS,
    Permission.VIEW_ALERTS,
    Permission.MANAGE_NOTICES,
    Permission.VIEW_AUDIT_LOG,
    Permission.MANAGE_USERS,
    Permission.VIEW_DOCUMENTS,
    Permission.MANAGE_DOCUMENT_TEMPLATES,
    Permission.REPORT_TO_UIF,
    Permission.VIEW_ACCOUNTS,
    Permission.VIEW_RECONCILIATION,
    Permission.VIEW_KYC_QUEUE,
    Permission.VIEW_COMPLIANCE_CASES,
    Permission.VIEW_REGULATORY_REPORTS,
    Permission.VIEW_PLD_SYNC,
    Permission.MANAGE_PLD_SYNC,
  ],

  [Role.COMPLIANCE_OFFICER]: [
    Permission.VIEW_PARTIES,
    Permission.VIEW_TRANSFERS,
    Permission.VIEW_ALERTS,
    Permission.RESOLVE_ALERTS,
    Permission.ESCALATE_ALERTS,
    Permission.VIEW_AUDIT_LOG,
    Permission.VIEW_DOCUMENTS,
    Permission.REPORT_TO_UIF,
    Permission.VIEW_KYC_QUEUE,
    Permission.VIEW_COMPLIANCE_CASES,
    Permission.VIEW_REGULATORY_REPORTS,
    Permission.VIEW_PLD_SYNC,
    Permission.MANAGE_PLD_SYNC,
  ],

  [Role.SUPPORT]: [
    Permission.VIEW_PARTIES,
    Permission.EDIT_PARTIES,
    Permission.VIEW_TRANSFERS,
    Permission.VIEW_ALERTS,
  ],

  [Role.VIEWER]: [Permission.VIEW_PARTIES, Permission.VIEW_TRANSFERS, Permission.VIEW_ALERTS],

  [Role.CLIENT]: [],

  // ── Backoffice roles ──────────────────────────────────────────────────────
  [Role.SYSTEM_ADMIN]: Object.values(Permission),

  [Role.BO_CEO]: [
    Permission.VIEW_PARTIES,
    Permission.VIEW_TRANSFERS,
    Permission.VIEW_ALERTS,
    Permission.VIEW_AUDIT_LOG,
    Permission.MANAGE_USERS,
    Permission.MANAGE_NOTICES,
  ],

  [Role.BO_CFO]: [
    Permission.VIEW_PARTIES,
    Permission.VIEW_TRANSFERS,
    Permission.VIEW_AUDIT_LOG,
    Permission.MANAGE_NOTICES,
    Permission.VIEW_ACCOUNTS,
    Permission.VIEW_RECONCILIATION,
    Permission.VIEW_REGULATORY_REPORTS,
  ],

  [Role.BO_CTO]: [Permission.MANAGE_USERS, Permission.VIEW_AUDIT_LOG],

  [Role.BO_CCO]: [
    Permission.VIEW_PARTIES,
    Permission.VIEW_TRANSFERS,
    Permission.VIEW_ALERTS,
    Permission.RESOLVE_ALERTS,
    Permission.ESCALATE_ALERTS,
    Permission.VIEW_AUDIT_LOG,
    Permission.MANAGE_NOTICES,
    Permission.VIEW_DOCUMENTS,
    Permission.REPORT_TO_UIF,
    Permission.VIEW_KYC_QUEUE,
    Permission.VIEW_COMPLIANCE_CASES,
    Permission.VIEW_REGULATORY_REPORTS,
    Permission.VIEW_PLD_SYNC,
    Permission.MANAGE_PLD_SYNC,
  ],

  [Role.BO_OPS_MANAGER]: [
    Permission.VIEW_PARTIES,
    Permission.EDIT_PARTIES,
    Permission.VIEW_TRANSFERS,
    Permission.VIEW_ALERTS,
    Permission.RESOLVE_ALERTS,
    Permission.VIEW_DOCUMENTS,
    Permission.VIEW_ACCOUNTS,
    Permission.VIEW_KYC_QUEUE,
  ],

  [Role.BO_KYC_REVIEWER]: [
    Permission.VIEW_PARTIES,
    Permission.VIEW_ALERTS,
    Permission.RESOLVE_ALERTS,
    Permission.VIEW_DOCUMENTS,
    Permission.VIEW_KYC_QUEUE,
  ],

  [Role.BO_KYC_MANAGER]: [
    Permission.VIEW_PARTIES,
    Permission.VIEW_ALERTS,
    Permission.RESOLVE_ALERTS,
    Permission.VIEW_DOCUMENTS,
    Permission.VIEW_KYC_QUEUE,
  ],

  [Role.BO_COMPLIANCE_OFFICER]: [
    Permission.VIEW_PARTIES,
    Permission.VIEW_TRANSFERS,
    Permission.VIEW_ALERTS,
    Permission.RESOLVE_ALERTS,
    Permission.ESCALATE_ALERTS,
    Permission.VIEW_AUDIT_LOG,
    Permission.VIEW_DOCUMENTS,
    Permission.REPORT_TO_UIF,
    Permission.VIEW_KYC_QUEUE,
    Permission.VIEW_COMPLIANCE_CASES,
    Permission.VIEW_REGULATORY_REPORTS,
    Permission.VIEW_PLD_SYNC,
    Permission.MANAGE_PLD_SYNC,
  ],

  [Role.BO_COMPLIANCE_ANALYST]: [
    Permission.VIEW_PARTIES,
    Permission.VIEW_TRANSFERS,
    Permission.VIEW_ALERTS,
    Permission.RESOLVE_ALERTS,
    Permission.VIEW_AUDIT_LOG,
    Permission.VIEW_DOCUMENTS,
    Permission.VIEW_KYC_QUEUE,
    Permission.VIEW_COMPLIANCE_CASES,
    Permission.VIEW_REGULATORY_REPORTS,
    Permission.VIEW_PLD_SYNC,
  ],

  [Role.BO_UIF_OFFICER]: [
    Permission.VIEW_PARTIES,
    Permission.VIEW_TRANSFERS,
    Permission.VIEW_AUDIT_LOG,
    Permission.VIEW_DOCUMENTS,
    Permission.REPORT_TO_UIF,
    Permission.VIEW_COMPLIANCE_CASES,
    Permission.VIEW_REGULATORY_REPORTS,
    Permission.VIEW_PLD_SYNC,
  ],

  [Role.BO_FRAUD_ANALYST]: [
    Permission.VIEW_PARTIES,
    Permission.VIEW_TRANSFERS,
    Permission.VIEW_ALERTS,
    Permission.VIEW_COMPLIANCE_CASES,
    Permission.VIEW_PLD_SYNC,
  ],

  [Role.BO_FRAUD_MANAGER]: [
    Permission.VIEW_PARTIES,
    Permission.VIEW_TRANSFERS,
    Permission.VIEW_ALERTS,
    Permission.VIEW_AUDIT_LOG,
    Permission.VIEW_COMPLIANCE_CASES,
    Permission.VIEW_PLD_SYNC,
  ],

  [Role.BO_FINANCE_ACCOUNTANT]: [Permission.VIEW_TRANSFERS, Permission.VIEW_AUDIT_LOG],

  [Role.BO_FINANCE_CONTROLLER]: [
    Permission.VIEW_TRANSFERS,
    Permission.VIEW_AUDIT_LOG,
    Permission.MANAGE_NOTICES,
    Permission.VIEW_ACCOUNTS,
    Permission.VIEW_RECONCILIATION,
  ],

  [Role.BO_TREASURY]: [Permission.VIEW_TRANSFERS],

  [Role.BO_SUPPORT_L1]: [Permission.VIEW_PARTIES, Permission.VIEW_TRANSFERS],

  [Role.BO_SUPPORT_L2]: [
    Permission.VIEW_PARTIES,
    Permission.EDIT_PARTIES,
    Permission.VIEW_TRANSFERS,
    Permission.VIEW_ALERTS,
    Permission.RESOLVE_ALERTS,
    Permission.VIEW_ACCOUNTS,
    Permission.VIEW_RECONCILIATION,
  ],

  [Role.BO_DEVELOPER]: [Permission.VIEW_AUDIT_LOG],
};

export function hasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

export function getPermissions(role: Role): Permission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}

// ============================================================================
// ENTITIES
// ============================================================================

// --- User (Backoffice) ---
export class User {
  constructor(
    public readonly id: string,
    public readonly email: string,
    public readonly firstName: string,
    public readonly lastName: string,
    public readonly role: Role,
    public readonly isActive: boolean = true,
    public readonly lastLoginAt?: Date,
    public readonly createdAt: Date = new Date(),
    public readonly permissions: Record<string, unknown> = {}
  ) {}

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  get initials(): string {
    return `${this.firstName.charAt(0)}${this.lastName.charAt(0)}`.toUpperCase();
  }

  /**
   * El servidor manda. `ROLE_PERMISSIONS` es sólo el respaldo para cuando
   * `/api/v1/admin/me` no trajo capacidades (usuarios internos legacy sin rol
   * RBAC, o una respuesta incompleta).
   *
   * Antes esto era un OR: si el mapa estático decía `true`, se concedía aunque
   * el servidor lo hubiera negado. Eso no es un respaldo, es una segunda
   * autoridad — y es lo que hacía que el menú ofreciera módulos que el backend
   * respondía con 403 (`bo_cco` tenía MANAGE_NOTICES en el mapa y ni un solo
   * permiso `bo_notifications.*` en el catálogo).
   *
   * Con capacidades presentes, la ausencia de una llave significa "no", no
   * "consulta la otra tabla".
   */
  can(permission: Permission): boolean {
    if (this.hasServerCapabilities) {
      return this.permissions[permission] === true;
    }

    return hasPermission(this.role, permission);
  }

  /** ¿El servidor envió el mapa de capacidades? */
  private get hasServerCapabilities(): boolean {
    return Object.keys(this.permissions).length > 0;
  }

  canAny(...permissions: Permission[]): boolean {
    return permissions.some(p => this.can(p));
  }

  canAll(...permissions: Permission[]): boolean {
    return permissions.every(p => this.can(p));
  }

  static fromApi(data: Record<string, unknown>): User {
    return new User(
      data.id as string,
      data.email as string,
      ((data.first_name ?? data.firstName) as string) || '',
      ((data.last_name ?? data.lastName) as string) || '',
      data.role as Role,
      ((data.is_active ?? data.isActive) as boolean) ?? true,
      (data.last_login_at ?? data.lastLoginAt)
        ? new Date((data.last_login_at ?? data.lastLoginAt) as string)
        : undefined,
      (data.created_at ?? data.createdAt)
        ? new Date((data.created_at ?? data.createdAt) as string)
        : new Date(),
      (data.permissions as Record<string, unknown>) || {}
    );
  }

  toJSON(): Record<string, unknown> {
    return {
      id: this.id,
      email: this.email,
      first_name: this.firstName,
      last_name: this.lastName,
      role: this.role,
      is_active: this.isActive,
      last_login_at: this.lastLoginAt?.toISOString(),
      created_at: this.createdAt.toISOString(),
      permissions: this.permissions,
    };
  }
}

// --- Party (Client) ---
/**
 * Cuenta del cliente para la vista customer-360 del backoffice (auditoría
 * jun-2026). Llega en `GET /api/v1/admin/parties/:id` → `accounts[]`. Montos en
 * centavos (bigint en BE).
 */
export interface PartyAccount {
  id: string;
  kind: string;
  status: string;
  currencyCode: string;
  clabe?: string;
  ledgerBalanceCents: number;
  availableBalanceCents: number;
  holdBalanceCents: number;
}

export class Party {
  constructor(
    public readonly id: string,
    public readonly email: string,
    public readonly phone: string,
    public readonly firstName: string,
    public readonly lastName: string,
    public readonly kycLevel: KYCLevel,
    public readonly status: PartyStatus,
    public readonly curp?: string,
    public readonly rfc?: string,
    public readonly blockedAt?: Date,
    public readonly blockedReason?: string,
    public readonly createdAt: Date = new Date(),
    /**
     * Tipo de persona canónico del usuario AUTENTICADO, tal como lo expone el BE
     * en `GET /api/v1/me` (`party.person_type`). Difiere del `personType` de
     * registro (sessionStorage 'individual'|'business'): este es la verdad del
     * backend una vez completado el onboarding. Opcional: el BE viejo / OAuth
     * userinfo no lo emiten.
     */
    public readonly personType?: 'pf' | 'pfae' | 'pm',
    /** Cuentas del cliente (customer-360). Solo viene en el detalle admin. */
    public readonly accounts: PartyAccount[] = [],
    /** Party 360: campos extendidos + provenance de migración (solo en el detalle admin). */
    public readonly detail?: Record<string, unknown>
  ) {}

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  get initials(): string {
    return `${this.firstName.charAt(0)}${this.lastName.charAt(0)}`.toUpperCase();
  }

  isActive(): boolean {
    return this.status === PartyStatus.ACTIVE;
  }

  isBlocked(): boolean {
    return this.status === PartyStatus.BLOCKED;
  }

  isPending(): boolean {
    return this.status === PartyStatus.PENDING;
  }

  canTransact(): boolean {
    return this.isActive() && this.kycLevel >= KYCLevel.BASIC;
  }

  static fromApi(data: Record<string, unknown>): Party {
    // Soportar full_name (API real) o first_name/last_name (mocks).
    // También aceptar camelCase (kycLevel, firstName…) para rehydration desde localStorage.
    let firstName = (data.first_name ?? data.firstName) as string | undefined;
    let lastName = (data.last_name ?? data.lastName) as string | undefined;

    if (!firstName && data.full_name) {
      const fullName = data.full_name as string;
      const parts = fullName.split(' ');
      firstName = parts[0] || '';
      lastName = parts.slice(1).join(' ') || '';
    }

    // Backend returns kyc_level as a string ("none","basic","intermediate","full").
    // localStorage stores the numeric enum value (kycLevel).
    // Map strings to the numeric enum so comparisons like kycLevel >= KYCLevel.BASIC work.
    const kycLevelStringMap: Record<string, KYCLevel> = {
      none: KYCLevel.NONE,
      basic: KYCLevel.BASIC,
      intermediate: KYCLevel.INTERMEDIATE,
      full: KYCLevel.FULL,
    };
    const rawKyc = data.kyc_level ?? data.kycLevel;
    const resolvedKycLevel: KYCLevel =
      typeof rawKyc === 'string'
        ? (kycLevelStringMap[rawKyc] ?? KYCLevel.NONE)
        : ((rawKyc as KYCLevel) ?? KYCLevel.NONE);

    // Customer-360: cuentas del cliente con balances + CLABE (solo en el detalle).
    const num = (v: unknown): number => (typeof v === 'number' ? v : Number(v ?? 0) || 0);
    const accounts: PartyAccount[] = Array.isArray(data.accounts)
      ? (data.accounts as Record<string, unknown>[]).map(a => ({
          id: String(a.id ?? ''),
          kind: String(a.kind ?? ''),
          status: String(a.status ?? ''),
          currencyCode: String(a.currency_code ?? a.currencyCode ?? ''),
          clabe: (a.clabe ?? undefined) as string | undefined,
          ledgerBalanceCents: num(a.ledger_balance_cents ?? a.ledgerBalanceCents),
          availableBalanceCents: num(a.available_balance_cents ?? a.availableBalanceCents),
          holdBalanceCents: num(a.hold_balance_cents ?? a.holdBalanceCents),
        }))
      : [];

    return new Party(
      ((data.party_id ?? data.partyId) as string) || (data.id as string),
      data.email as string,
      (data.phone as string) ?? '',
      firstName ?? '',
      lastName ?? '',
      resolvedKycLevel,
      (data.status as PartyStatus) ?? PartyStatus.ACTIVE,
      data.curp as string | undefined,
      (data.rfc ?? data.tax_id) as string | undefined,
      (data.blocked_at ?? data.blockedAt)
        ? new Date((data.blocked_at ?? data.blockedAt) as string)
        : undefined,
      (data.blocked_reason ?? data.blockedReason) as string | undefined,
      (data.created_at ?? data.createdAt)
        ? new Date((data.created_at ?? data.createdAt) as string)
        : new Date(),
      (data.person_type ?? data.personType) as 'pf' | 'pfae' | 'pm' | undefined,
      accounts,
      data
    );
  }

  /**
   * Crea un Party desde la respuesta de OAuth /userinfo endpoint.
   * Usado después del login MFA cuando no hay endpoint /auth/me disponible.
   */
  static fromUserInfo(info: {
    sub: string;
    name?: string;
    email?: string;
    phone_number?: string;
  }): Party {
    const nameParts = (info.name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    return new Party(
      info.sub,
      info.email || '',
      info.phone_number || '',
      firstName,
      lastName,
      KYCLevel.NONE, // Se actualizará cuando se cargue el perfil completo
      PartyStatus.ACTIVE,
      undefined,
      undefined,
      undefined,
      undefined,
      new Date()
    );
  }

  toJSON(): Record<string, unknown> {
    return {
      id: this.id,
      email: this.email,
      phone: this.phone,
      first_name: this.firstName,
      last_name: this.lastName,
      kyc_level: this.kycLevel,
      status: this.status,
      curp: this.curp,
      rfc: this.rfc,
      blocked_at: this.blockedAt?.toISOString(),
      blocked_reason: this.blockedReason,
      created_at: this.createdAt.toISOString(),
      person_type: this.personType,
    };
  }
}

// --- Account ---
export class Account {
  constructor(
    public readonly id: string,
    public readonly partyId: string,
    public readonly clabe: string,
    public readonly availableBalance: Money,
    public readonly heldBalance: Money,
    public readonly currency: string = 'MXN',
    public readonly isDefault: boolean = false,
    public readonly createdAt: Date = new Date(),
    public readonly accountNumber?: string,
    public readonly bankName?: string,
    /**
     * CLABE externa del PM (STP) — sólo presente en cuentas Persona Moral
     * provisionadas (el BE la expone en GetAccountsByPartyQuery). Da fallback
     * REST al evento WS `onboarding.clabe_ready`.
     */
    public readonly externalClabe?: string,
    public readonly externalBankName?: string,
    /**
     * Nombre legal del titular (`holder_name`, RFC 0038): razón social en PM,
     * nombre compuesto en PF. Sólo lo traen `GET /accounts` y
     * `GET /accounts/primary`; `GET /accounts/{id}` no incluye la clave.
     * `null` = sin nombre capturado (la UI oculta la fila). Es PII: no
     * persistir ni mandar a analítica.
     */
    public readonly holderName: string | null = null
  ) {}

  get totalBalance(): Money {
    return this.availableBalance.add(this.heldBalance);
  }

  hasAvailableFunds(amount: Money): boolean {
    return this.availableBalance.greaterThanOrEqual(amount);
  }

  static fromApi(data: Record<string, unknown>): Account {
    return new Account(
      data.id as string,
      data.party_id as string,
      data.clabe as string,
      Money.fromCents(
        ((data.available_balance_cents as number | undefined) ??
          (data.available_cents as number | undefined) ??
          0) as number
      ),
      Money.fromCents(
        ((data.held_balance_cents as number | undefined) ??
          (data.held_cents as number | undefined) ??
          0) as number
      ),
      (data.currency as string) ?? 'MXN',
      (data.is_default as boolean | undefined) ?? (data.is_primary as boolean | undefined) ?? false,
      data.created_at ? new Date(data.created_at as string) : new Date(),
      (data.account_number ?? data.accountNumber) as string | undefined,
      (data.bank_name ?? data.bankName) as string | undefined,
      (data.external_clabe ?? data.externalClabe) as string | undefined,
      (data.external_bank_name ?? data.externalBankName) as string | undefined,
      normalizeHolderName(data.holder_name ?? data.holderName)
    );
  }
}

function normalizeHolderName(raw: unknown): string | null {
  return typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : null;
}

// --- Transfer ---
export class Transfer {
  constructor(
    public readonly id: string,
    public readonly amount: Money,
    public readonly status: TransferStatus,
    public readonly type: TransferType,
    public readonly displayName: string,
    public readonly recipientName: string,
    public readonly recipientClabe: string,
    public readonly contractNumber: string,
    public readonly productType: 'transfer' = 'transfer',
    public readonly concept: string = '', // Backward compatibility
    public readonly recipientBank?: string,
    public readonly trackingKey?: string,
    public readonly failureReason?: string,
    public readonly createdAt: Date = new Date(),
    public readonly completedAt?: Date
  ) {}

  /**
   * Returns the amount formatted as a string (e.g., "5,000.00 MXN")
   */
  get formattedAmount(): string {
    return this.amount.format();
  }

  isPending(): boolean {
    return this.status === TransferStatus.PENDING;
  }

  isProcessing(): boolean {
    return this.status === TransferStatus.PROCESSING;
  }

  isCompleted(): boolean {
    return this.status === TransferStatus.COMPLETED;
  }

  isFailed(): boolean {
    return this.status === TransferStatus.FAILED;
  }

  isCancelled(): boolean {
    return this.status === TransferStatus.CANCELLED;
  }

  canCancel(): boolean {
    return this.status === TransferStatus.PENDING;
  }

  static fromApi(data: Record<string, unknown>): Transfer {
    // Support both old (concept) and new (display_name) field names
    const displayName = (data.display_name || data.concept) as string;

    return new Transfer(
      data.id as string,
      Money.fromCents(data.amount_cents as number),
      data.status as TransferStatus,
      (data.type as TransferType) ?? TransferType.SPEI,
      displayName,
      data.recipient_name as string,
      data.recipient_clabe as string,
      (data.contract_number || data.transaction_id) as string,
      (data.product_type as 'transfer') ?? 'transfer',
      data.concept as string, // Backward compatibility
      data.recipient_bank as string | undefined,
      data.tracking_key as string | undefined,
      data.failure_reason as string | undefined,
      data.created_at ? new Date(data.created_at as string) : new Date(),
      data.completed_at ? new Date(data.completed_at as string) : undefined
    );
  }
}

// --- Beneficiary ---
export class Beneficiary {
  constructor(
    public readonly id: string,
    public readonly partyId: string,
    public readonly alias: string,
    public readonly name: string,
    public readonly clabe: string,
    public readonly bank: string,
    public readonly email?: string,
    public readonly isVerified: boolean = false,
    public readonly createdAt: Date = new Date()
  ) {}

  static fromApi(data: Record<string, unknown>): Beneficiary {
    return new Beneficiary(
      data.id as string,
      data.party_id as string,
      data.alias as string,
      data.name as string,
      data.clabe as string,
      data.bank as string,
      data.email as string | undefined,
      (data.is_verified as boolean) ?? false,
      data.created_at ? new Date(data.created_at as string) : new Date()
    );
  }
}

// --- Alert ---
export class Alert {
  constructor(
    public readonly id: string,
    public readonly type: AlertType,
    public readonly severity: AlertSeverity,
    public readonly status: AlertStatus,
    public readonly partyId: string,
    public readonly partyName: string,
    public readonly description: string,
    public readonly riskScore: number,
    public readonly metadata: Record<string, unknown> = {},
    public readonly assignedTo?: string,
    public readonly resolvedBy?: string,
    public readonly resolvedAt?: Date,
    public readonly resolutionNotes?: string,
    public readonly createdAt: Date = new Date()
  ) {}

  isPending(): boolean {
    return this.status === AlertStatus.PENDING;
  }

  isInvestigating(): boolean {
    return this.status === AlertStatus.INVESTIGATING;
  }

  isResolved(): boolean {
    return this.status === AlertStatus.RESOLVED || this.status === AlertStatus.DISMISSED;
  }

  isCritical(): boolean {
    return this.severity === AlertSeverity.CRITICAL;
  }

  isHigh(): boolean {
    return this.severity === AlertSeverity.HIGH || this.severity === AlertSeverity.CRITICAL;
  }

  static fromApi(data: Record<string, unknown>): Alert {
    return new Alert(
      data.id as string,
      data.type as AlertType,
      data.severity as AlertSeverity,
      data.status as AlertStatus,
      data.party_id as string,
      data.party_name as string,
      data.description as string,
      (data.risk_score as number) ?? 0,
      (data.metadata as Record<string, unknown>) ?? {},
      data.assigned_to as string | undefined,
      data.resolved_by as string | undefined,
      data.resolved_at ? new Date(data.resolved_at as string) : undefined,
      data.resolution_notes as string | undefined,
      data.created_at ? new Date(data.created_at as string) : new Date()
    );
  }
}

// --- System Notice ---
export class SystemNotice {
  constructor(
    public readonly id: string,
    public readonly type: NoticeType,
    public readonly severity: NoticeSeverity,
    public readonly status: NoticeStatus,
    public readonly target: NoticeTarget,
    public readonly title: string,
    public readonly message: string,
    public readonly dismissible: boolean,
    public readonly startsAt: Date,
    public readonly endsAt?: Date,
    public readonly maintenanceStartsAt?: Date,
    public readonly maintenanceEndsAt?: Date,
    public readonly link?: string,
    public readonly linkText?: string,
    public readonly createdBy?: string,
    public readonly createdAt: Date = new Date()
  ) {}

  isActive(): boolean {
    const now = new Date();
    const started = now >= this.startsAt;
    const notEnded = !this.endsAt || now <= this.endsAt;
    return this.status === NoticeStatus.ACTIVE && started && notEnded;
  }

  isScheduled(): boolean {
    return this.status === NoticeStatus.SCHEDULED && new Date() < this.startsAt;
  }

  isExpired(): boolean {
    return this.endsAt ? new Date() > this.endsAt : false;
  }

  isMaintenance(): boolean {
    return this.type === NoticeType.MAINTENANCE;
  }

  getTimeUntilMaintenance(): number | null {
    if (!this.maintenanceStartsAt) return null;
    return Math.max(0, this.maintenanceStartsAt.getTime() - Date.now());
  }

  static fromApi(data: Record<string, unknown>): SystemNotice {
    return new SystemNotice(
      data.id as string,
      data.type as NoticeType,
      data.severity as NoticeSeverity,
      data.status as NoticeStatus,
      data.target as NoticeTarget,
      data.title as string,
      data.message as string,
      (data.dismissible as boolean) ?? true,
      new Date(data.starts_at as string),
      data.ends_at ? new Date(data.ends_at as string) : undefined,
      data.maintenance_starts_at ? new Date(data.maintenance_starts_at as string) : undefined,
      data.maintenance_ends_at ? new Date(data.maintenance_ends_at as string) : undefined,
      data.link as string | undefined,
      data.link_text as string | undefined,
      data.created_by as string | undefined,
      data.created_at ? new Date(data.created_at as string) : new Date()
    );
  }
}

// --- Audit Event ---
export class AuditEvent {
  constructor(
    public readonly id: string,
    public readonly action: AuditAction,
    public readonly category: AuditCategory,
    public readonly actorId: string,
    public readonly actorName: string,
    public readonly actorType: 'user' | 'party' | 'system',
    public readonly ipAddress: string,
    public readonly resourceType?: string,
    public readonly resourceId?: string,
    public readonly userAgent?: string,
    public readonly metadata: Record<string, unknown> = {},
    public readonly createdAt: Date = new Date(),
    /** Etiqueta en español que ya calcula el backend (`action_label`). */
    public readonly actionLabel?: string
  ) {}

  /**
   * El backend (`/api/v1/admin/audit-logs`) nunca emitió `category`,
   * `actor_name`, `resource_type` ni `created_at`. Este mapeo leía esos cuatro
   * y obtenía `undefined` en todos: la tabla salía con columnas en blanco y
   * "Invalid Date" en la fecha. Los nombres reales del contrato son
   * `actor_summary`, `target_type` y `occurred_at`.
   *
   * `category` no existe como concepto en el backend; se conserva el campo por
   * compatibilidad de firma, pero llega vacío y no debe pintarse.
   */
  static fromApi(data: Record<string, unknown>): AuditEvent {
    const actor = (data.actor_summary ?? {}) as { name?: string; email?: string };

    return new AuditEvent(
      data.id as string,
      data.action as AuditAction,
      data.category as AuditCategory,
      data.actor_id as string,
      actor.name ?? actor.email ?? '',
      (data.actor_type as 'user' | 'party' | 'system') ?? 'user',
      (data.ip_address as string) ?? '',
      data.target_type as string | undefined,
      data.target_id as string | undefined,
      data.user_agent as string | undefined,
      (data.metadata as Record<string, unknown>) ?? {},
      data.occurred_at ? new Date(data.occurred_at as string) : new Date(),
      data.action_label as string | undefined
    );
  }
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface PaginationMeta {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

export interface AuthResponse {
  token: string;
  refresh_token: string;
  expires_at: string;
  user?: Record<string, unknown>;
  party?: Record<string, unknown>;
}

export interface DashboardMetrics {
  totalParties: number;
  activeParties: number;
  todayVolume: Money;
  todayTransfers: number;
  pendingAlerts: number;
  activeNotices: number;
  monthlyGrowth: number;
}

// ============================================================
// ROTATING SAVINGS (TANDAS)
// ============================================================

export enum RotatingSavingPeriod {
  WEEKLY = 'weekly',
  BIWEEKLY = 'biweekly',
  MONTHLY = 'monthly',
}

export enum RotatingSavingStatus {
  ACTIVE = 'active',
  PENDING = 'pending',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export const ROTATING_SAVING_PERIOD_LABELS: Record<RotatingSavingPeriod, string> = {
  [RotatingSavingPeriod.WEEKLY]: 'Semanal',
  [RotatingSavingPeriod.BIWEEKLY]: 'Quincenal',
  [RotatingSavingPeriod.MONTHLY]: 'Mensual',
};

export const ROTATING_SAVING_STATUS_LABELS: Record<RotatingSavingStatus, string> = {
  [RotatingSavingStatus.ACTIVE]: 'Activa',
  [RotatingSavingStatus.PENDING]: 'Pendiente',
  [RotatingSavingStatus.COMPLETED]: 'Completada',
  [RotatingSavingStatus.CANCELLED]: 'Cancelada',
};

export class RotatingSaving {
  constructor(
    public readonly id: string,
    public readonly folio: string,
    public readonly name: string,
    public readonly period: RotatingSavingPeriod,
    public readonly startDate: Date,
    public readonly endDate: Date,
    public readonly amount: Money,
    public readonly contribution: Money,
    public readonly bonus: Money,
    public readonly status: RotatingSavingStatus,
    public readonly link?: string
  ) {}

  isActive(): boolean {
    return this.status === RotatingSavingStatus.ACTIVE;
  }

  isPending(): boolean {
    return this.status === RotatingSavingStatus.PENDING;
  }

  isCompleted(): boolean {
    return this.status === RotatingSavingStatus.COMPLETED;
  }

  isCancelled(): boolean {
    return this.status === RotatingSavingStatus.CANCELLED;
  }

  static fromApi(data: Record<string, unknown>): RotatingSaving {
    return new RotatingSaving(
      data.id as string,
      data.folio as string,
      data.name as string,
      data.period as RotatingSavingPeriod,
      new Date(data.start_date as string),
      new Date(data.end_date as string),
      Money.fromCents(data.amount_cents as number),
      Money.fromCents(data.contribution_cents as number),
      Money.fromCents(data.bonus_cents as number),
      data.status as RotatingSavingStatus,
      data.link as string | undefined
    );
  }
}

// ============================================================================
// CONTACTS
// ============================================================================

export enum ContactType {
  EXTERNAL = 'external',
  TANKEF = 'tankef',
}

export const CONTACT_TYPE_LABELS: Record<ContactType, string> = {
  [ContactType.EXTERNAL]: 'Externo',
  [ContactType.TANKEF]: 'Red Tankef',
};

export interface ContactData {
  id: string;
  type: ContactType;
  clabe?: string;
  bankCode?: string;
  bankName?: string;
  firstName: string;
  lastName: string;
  alias?: string;
  email?: string;
  phone?: string;
  avatarUrl?: string;
  createdAt: Date;
}

export interface ContactSummary {
  totalCount: number;
  externalCount: number;
  tankefCount: number;
}

export class Contact {
  constructor(
    public readonly id: string,
    public readonly type: ContactType,
    public readonly firstName: string,
    public readonly lastName: string,
    public readonly clabe?: string,
    public readonly bankCode?: string,
    public readonly bankName?: string,
    public readonly alias?: string,
    public readonly email?: string,
    public readonly phone?: string,
    public readonly avatarUrl?: string,
    public readonly createdAt: Date = new Date()
  ) {}

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  get displayName(): string {
    return this.alias || this.fullName;
  }

  get initials(): string {
    return `${this.firstName.charAt(0)}${this.lastName.charAt(0)}`.toUpperCase();
  }

  get maskedClabe(): string {
    if (!this.clabe) return '';
    return `****${this.clabe.slice(-4)}`;
  }

  isExternal(): boolean {
    return this.type === ContactType.EXTERNAL;
  }

  isTankef(): boolean {
    return this.type === ContactType.TANKEF;
  }

  static fromApi(data: Record<string, unknown>): Contact {
    return new Contact(
      data.id as string,
      data.type as ContactType,
      data.first_name as string,
      data.last_name as string,
      data.clabe as string | undefined,
      data.bank_code as string | undefined,
      data.bank_name as string | undefined,
      data.alias as string | undefined,
      data.email as string | undefined,
      data.phone as string | undefined,
      data.avatar_url as string | undefined,
      data.created_at ? new Date(data.created_at as string) : new Date()
    );
  }

  toJSON(): Record<string, unknown> {
    return {
      id: this.id,
      type: this.type,
      first_name: this.firstName,
      last_name: this.lastName,
      clabe: this.clabe,
      bank_code: this.bankCode,
      bank_name: this.bankName,
      alias: this.alias,
      email: this.email,
      phone: this.phone,
      avatar_url: this.avatarUrl,
      created_at: this.createdAt.toISOString(),
    };
  }
}

// ============================================================================
// NOTIFICATIONS
// ============================================================================

/** Categories of notifications as defined by the API */
export type NotificationCategory =
  | 'transactional'
  | 'compliance'
  | 'security'
  | 'system'
  | 'product_lifecycle'
  | 'promotional'
  | 'informational';

/** Priority levels for notifications */
export type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent';

/** Notification entity data aligned with API spec */
export interface NotificationData {
  /** Unique notification ID */
  id: string;
  /** Type of notification (e.g., 'transfer_completed', 'security_alert') */
  notificationType: string;
  /** Category of notification */
  category: NotificationCategory;
  /** Notification title */
  title: string;
  /** Notification body/message */
  body: string;
  /** Whether the notification has been read */
  read: boolean;
  /** Priority level */
  priority: NotificationPriority;
  /** Whether this notification is mandatory (cannot be disabled) */
  mandatory: boolean;
  /** When the notification was read */
  readAt: Date | null;
  /** When the notification was created */
  createdAt: Date;
  /** URL to navigate to when clicking the notification */
  actionUrl: string | null;
  /** Channel through which notification was sent */
  channel: string;
  /** Status of the notification */
  status: string;
  /** Additional contextual data */
  contextData: Record<string, unknown>;
  /** When the notification was sent */
  sentAt: Date | null;
  /** When the notification expires */
  expiresAt: Date | null;
}

/** API response for notification */
export interface NotificationApiResponse {
  id: string;
  notification_type: string;
  category: string;
  title: string;
  body: string;
  read: boolean;
  priority: string;
  mandatory: boolean;
  read_at: string | null;
  created_at: string;
  action_url: string | null;
  channel: string;
  status: string;
  context_data: Record<string, unknown>;
  sent_at: string | null;
  expires_at: string | null;
}

/** Pagination metadata from API */
export interface NotificationsPaginationMeta {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  perPage: number;
  unreadCount: number;
}

/** Paginated notifications response */
export interface NotificationsResponse {
  data: NotificationData[];
  meta: NotificationsPaginationMeta;
}

/** Channel preference with per-category settings */
export interface NotificationChannelPreference {
  /** Whether this channel is enabled */
  enabled: boolean;
  /** Category-specific settings for this channel */
  categories: Record<string, boolean>;
}

/** Information about a notification category */
export interface NotificationCategoryInfo {
  /** Whether this category is mandatory (cannot be disabled) */
  mandatory: boolean;
  /** Default channels for this category */
  defaultChannels: string[];
  /** Description of the category */
  description: string;
}

/** User notification preferences aligned with API spec */
export interface NotificationPreferences {
  /** Per-channel preferences (email, push, sms) */
  channels: Record<string, NotificationChannelPreference>;
  /** Information about each category */
  categories: Record<string, NotificationCategoryInfo>;
  /** List of mandatory categories that cannot be disabled */
  mandatoryCategories: string[];
  /** List of optional categories that can be toggled */
  optionalCategories: string[];
}

/** API response for notification preferences */
export interface NotificationPreferencesApiResponse {
  channels: Record<
    string,
    {
      enabled: boolean;
      categories: Record<string, boolean>;
    }
  >;
  categories: Record<
    string,
    {
      mandatory: boolean;
      default_channels: string[];
      description: string;
    }
  >;
  mandatory_categories: string[];
  optional_categories: string[];
}

/** Convert API notification to domain entity */
export function notificationFromApi(data: NotificationApiResponse): NotificationData {
  return {
    id: data.id,
    notificationType: data.notification_type,
    category: data.category as NotificationCategory,
    title: data.title,
    body: data.body,
    read: data.read,
    priority: data.priority as NotificationPriority,
    mandatory: data.mandatory,
    readAt: data.read_at ? new Date(data.read_at) : null,
    createdAt: new Date(data.created_at),
    actionUrl: data.action_url,
    channel: data.channel,
    status: data.status,
    contextData: data.context_data || {},
    sentAt: data.sent_at ? new Date(data.sent_at) : null,
    expiresAt: data.expires_at ? new Date(data.expires_at) : null,
  };
}

/** Convert API preferences to domain entity */
export function notificationPreferencesFromApi(
  data: NotificationPreferencesApiResponse
): NotificationPreferences {
  const channels: Record<string, NotificationChannelPreference> = {};
  for (const [channelName, channelData] of Object.entries(data.channels)) {
    channels[channelName] = {
      enabled: channelData.enabled,
      categories: channelData.categories,
    };
  }

  const categories: Record<string, NotificationCategoryInfo> = {};
  for (const [categoryName, categoryData] of Object.entries(data.categories)) {
    categories[categoryName] = {
      mandatory: categoryData.mandatory,
      defaultChannels: categoryData.default_channels,
      description: categoryData.description,
    };
  }

  return {
    channels,
    categories,
    mandatoryCategories: data.mandatory_categories,
    optionalCategories: data.optional_categories,
  };
}

/** Convert domain preferences to API format for update */
export function notificationPreferencesToApi(
  prefs: Partial<NotificationPreferences>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if (prefs.channels) {
    const channels: Record<string, { enabled: boolean; categories?: Record<string, boolean> }> = {};
    for (const [channelName, channelData] of Object.entries(prefs.channels)) {
      channels[channelName] = {
        enabled: channelData.enabled,
      };
      if (Object.keys(channelData.categories).length > 0) {
        channels[channelName].categories = channelData.categories;
      }
    }
    result.channels = channels;
  }

  return result;
}

// ============================================================================
// JAAK IDENTITY VERIFICATION TYPES
// ============================================================================
export * from './api';

// ============================================================================
// DOMAIN ENTITIES
// ============================================================================
export {
  OnboardingSession,
  type OnboardingBlock,
  type OnboardingBlockKey,
  type OnboardingBlockStatus,
  type OnboardingBlocks,
  type OnboardingNextStep,
  type OnboardingSessionData,
} from './entities/onboarding-session';

// ============================================================================
// PARTY TYPE (PF / PM discriminator)
// ============================================================================

// Movido a domain/onboarding/types.ts (F0 plan Persona Moral). Re-export para
// preservar los imports existentes desde @tankef/core sin romper call-sites.
export type { PartyTypeFE, SubjectKindBE } from './onboarding/types';
export { partyTypeToKind, kindToPartyType } from './onboarding/types';

// Abstracción OnboardingFlow (F1 plan Persona Moral). Interfaces puras.
export type {
  OnboardingFlow,
  OnboardingFlowStep,
  SessionSnapshot,
  StepContext,
} from './onboarding/flow';

// ============================================================================
// ERROR HANDLING & RESULT PATTERN
// ============================================================================
export * from './error-handling';

// ============================================================================
// ORGANIGRAMA (estructura organizacional PM)
// ============================================================================
export * from './org-chart';

// ============================================================================
// ESTRUCTURA ACCIONARIA (PM)
// ============================================================================
export * from './shareholder-structure';

export * from './reports';
